import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useT } from '@/i18n/use-t';
import {
  useApplyProvisionBindingLink,
  useApplyProvisionEquipmentLink,
  useApplyProvisionManualHardware,
  usePreviewProvisionBindingLink,
  usePreviewProvisionEquipmentLink,
  usePreviewProvisionManualHardware,
  useProvisionEndpointInventory,
  useProvisionEquipmentCatalogue,
  useProvisionLegacyMatchProposals,
  useProvisionManualHardware,
  useProvisionManualHardwareHistory,
  useProvisionSoftwareCatalogue,
  type ProvisionBindingLinkReferences,
  type ProvisionEquipmentCatalogueModel,
  type ProvisionEquipmentLinkPreview,
  type ProvisionLegacyMatchProposal,
  type ProvisionManualUpdateInput,
  type ProvisionPeripheralManualRecord,
  type ProvisionRawFieldDifference,
  type ProvisionSoftwareCatalogueComponent,
} from '@/hooks/use-provision';
import { redactForDisplay } from './advanced-composition';
import { certificationLabel } from './certification-label';

export function AdvancedHardwarePanel({
  deviceId,
  onClose,
}: {
  deviceId: string;
  onClose: () => void;
}) {
  const t = useT('admin_ui');
  const record = useProvisionManualHardware(deviceId);
  const history = useProvisionManualHardwareHistory(deviceId);
  const proposals = useProvisionLegacyMatchProposals(deviceId);
  const equipment = useProvisionEquipmentCatalogue(null, { enabled: true });
  const software = useProvisionSoftwareCatalogue(null, { enabled: true });
  const inventory = useProvisionEndpointInventory(record.data?.endpointId ?? null);
  const previewUpdate = usePreviewProvisionManualHardware();
  const applyUpdate = useApplyProvisionManualHardware();
  const initializedVersion = useRef<number | null>(null);
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof previewUpdate.mutateAsync>
  > | null>(null);
  const [reason, setReason] = useState('');
  const [applyMessage, setApplyMessage] = useState<string | null>(null);

  useEffect(() => {
    if (record.data === undefined || initializedVersion.current === record.data.version) return;
    initializedVersion.current = record.data.version;
    setText(prettyJson(manualHardwareEditable(record.data)));
    setPreview(null);
    setApplyMessage(null);
    setReason('');
    setParseError(null);
  }, [record.data]);

  function parsedValues() {
    if (record.data === undefined) return null;
    const parsed = parseManualHardwareText(text, record.data);
    setParseError(parsed.ok ? null : parsed.error);
    return parsed.ok ? parsed.values : null;
  }

  async function generatePreview() {
    const values = parsedValues();
    if (values === null) return;
    setApplyMessage(null);
    setPreview(await previewUpdate.mutateAsync({ deviceId, values }));
  }

  async function apply() {
    const values = parsedValues();
    if (values === null || preview === null) return;
    const result = await applyUpdate.mutateAsync({
      deviceId,
      values,
      previewChecksum: preview.previewChecksum,
      reason,
    });
    initializedVersion.current = result.record.version;
    setText(prettyJson(manualHardwareEditable(result.record)));
    setPreview(null);
    setApplyMessage(
      (result.changed
        ? t(
            'provision.hardware.appliedMessage',
            'Manual values applied at aggregate version {version}. Desired state will publish through the normal resolver.',
          )
        : t(
            'provision.hardware.noChangesMessage',
            'No changes detected. Version {version}, audit, bindings, and desired-state revision remain unchanged.',
          )
      ).replace('{version}', String(result.record.version)),
    );
  }

  return (
    <section className="space-y-5 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold">
              {t('provision.hardware.title', 'Advanced/manual hardware detail')}
            </h2>
            {record.data !== undefined && <MatchBadge status={record.data.catalogueMatchStatus} />}
            <Badge variant="outline">
              {t('provision.hardware.adminOnly', 'Provision Admin only')}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'provision.hardware.help',
              'Complete raw device and stable binding identities. Catalogue and port references are compared separately and never rewrite these runtime values.',
            )}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onClose}>
          {t('provision.hardware.closeDetail', 'Close detail')}
        </Button>
      </div>

      {record.isLoading && (
        <p className="text-sm text-muted-foreground">
          {t('provision.hardware.loadingRecord', 'Loading raw record…')}
        </p>
      )}
      {record.data !== undefined && (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">
              {t('provision.hardware.badgeVersion', 'version {version}').replace(
                '{version}',
                String(record.data.version),
              )}
            </Badge>
            <Badge variant="outline">{record.data.lifecycle}</Badge>
            <Badge variant="outline">
              {t('provision.hardware.badgeEndpoint', 'endpoint {endpoint}').replace(
                '{endpoint}',
                record.data.endpointId ?? t('provision.hardware.unassigned', 'unassigned'),
              )}
            </Badge>
            <Badge variant="outline">
              {t('provision.hardware.badgeModelRef', 'model ref {model}').replace(
                '{model}',
                record.data.equipmentModelRevisionId ?? t('provision.hardware.none', 'none'),
              )}
            </Badge>
          </div>
          <Textarea
            aria-label={t('provision.hardware.jsonLabel', 'Complete Advanced manual hardware JSON')}
            className="min-h-[420px] bg-background font-mono text-xs"
            spellCheck={false}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setPreview(null);
              setApplyMessage(null);
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={previewUpdate.isPending || applyUpdate.isPending}
              onClick={() => void generatePreview()}
            >
              {t('provision.hardware.previewRecord', 'Preview complete raw record')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setText(prettyJson(manualHardwareEditable(record.data!)));
                setPreview(null);
                setParseError(null);
              }}
            >
              {t('provision.hardware.resetUnchanged', 'Reset unchanged value')}
            </Button>
          </div>
        </>
      )}
      {parseError !== null && <ErrorPanel message={parseError} />}
      <MutationError error={record.error ?? previewUpdate.error ?? applyUpdate.error} />
      {preview !== null && (
        <div className="space-y-3 rounded-md border bg-background p-3">
          <div className="flex flex-wrap gap-2">
            <Badge variant={preview.changed ? 'outline' : 'success'}>
              {preview.changed
                ? t('provision.hardware.changesCount', '{count} changes').replace(
                    '{count}',
                    String(preview.differences.length),
                  )
                : t('provision.hardware.unchangedNoOp', 'unchanged no-op')}
            </Badge>
            {preview.blocked && (
              <Badge variant="destructive">
                {t('provision.hardware.activeOperationBlocked', 'active-operation blocked')}
              </Badge>
            )}
            {preview.activeOperations > 0 && (
              <Badge variant="outline">
                {t('provision.hardware.activeOperations', '{count} active operations').replace(
                  '{count}',
                  String(preview.activeOperations),
                )}
              </Badge>
            )}
          </div>
          {!preview.changed && (
            <p className="text-sm">
              {t(
                'provision.hardware.noDifferenceHelp',
                'The server compared the full aggregate and found no difference. Confirming this exact payload will not increment a version, write audit, touch bindings, or publish desired state.',
              )}
            </p>
          )}
          {preview.differences.length > 0 && <DifferenceList differences={preview.differences} />}
          <Input
            aria-label={t('provision.hardware.reasonLabel', 'Manual hardware change reason')}
            required
            placeholder={
              preview.changed
                ? t('provision.hardware.reasonChangePlaceholder', 'Reason for raw value change')
                : t('provision.hardware.reasonNoOpPlaceholder', 'Reason for no-op check')
            }
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button
            disabled={preview.blocked || applyUpdate.isPending || reason.trim().length === 0}
            onClick={() => void apply()}
          >
            {preview.changed
              ? t('provision.hardware.confirmUpdate', 'Confirm audited manual update')
              : t('provision.hardware.confirmNoOp', 'Confirm unchanged no-op')}
          </Button>
        </div>
      )}
      {applyMessage !== null && (
        <p className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm">
          {applyMessage}
        </p>
      )}

      {record.data !== undefined && (
        <section className="space-y-3 border-t pt-4">
          <div>
            <h3 className="font-semibold">
              {t('provision.hardware.reconciliation', 'Catalogue reconciliation')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t(
                'provision.hardware.reconciliationHelp',
                'Exact, ambiguous, or absent proposals are evidence only. Preview and explicit confirmation add or remove nullable references without changing raw fields.',
              )}
            </p>
          </div>
          <DeviceLinkPanel
            record={record.data}
            proposal={proposals.data?.device ?? null}
            catalogue={equipment.data ?? []}
          />
          {record.data.bindings.map((binding) => (
            <BindingLinkPanel
              key={binding.bindingId}
              record={record.data!}
              binding={binding}
              proposal={
                proposals.data?.bindings.find((item) => item.entityId === binding.bindingId) ?? null
              }
              software={software.data ?? []}
              ports={inventory.data?.ports ?? []}
            />
          ))}
          <MutationError
            error={proposals.error ?? equipment.error ?? software.error ?? inventory.error}
          />
        </section>
      )}

      <section className="border-t pt-4">
        <h3 className="font-semibold">
          {t('provision.hardware.auditHistory', 'Redacted immutable audit history')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t(
            'provision.hardware.auditHistoryHelp',
            'Manual edits and reference changes include before/after evidence, reasons, and immutable catalogue snapshots. Secret-looking material is redacted by the API.',
          )}
        </p>
        <div className="mt-3 space-y-2">
          {history.data?.map((entry) => (
            <details key={entry.auditId} className="rounded-md border bg-background p-3">
              <summary className="cursor-pointer text-sm">
                <strong>{entry.action}</strong> · {new Date(entry.occurredAt).toLocaleString()} ·{' '}
                {entry.actor.displayName ??
                  entry.actor.email ??
                  entry.actor.id ??
                  t('provision.composition.actorSystem', 'system')}
              </summary>
              <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                {prettyJson({ before: entry.before, after: entry.after, context: entry.context })}
              </pre>
            </details>
          ))}
          {history.isSuccess && history.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('provision.hardware.noAuditEntries', 'No audit entries for this aggregate yet.')}
            </p>
          )}
          <MutationError error={history.error} />
        </div>
      </section>
    </section>
  );
}

function DeviceLinkPanel({
  record,
  proposal,
  catalogue,
}: {
  record: ProvisionPeripheralManualRecord;
  proposal: ProvisionLegacyMatchProposal | null;
  catalogue: ProvisionEquipmentCatalogueModel[];
}) {
  const t = useT('admin_ui');
  const previewLink = usePreviewProvisionEquipmentLink();
  const applyLink = useApplyProvisionEquipmentLink();
  const [selected, setSelected] = useState(record.equipmentModelRevisionId ?? '');
  const [preview, setPreview] = useState<ProvisionEquipmentLinkPreview | null>(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [reason, setReason] = useState('');
  const candidates =
    proposal?.candidates.filter((candidate) => candidate.kind === 'equipment_model') ?? [];
  const displayName = (id: string) => {
    for (const model of catalogue) {
      const revision = model.revisions.find((item) => item.id === id);
      if (revision !== undefined)
        return `${revision.manufacturer} ${revision.model} r${revision.revision}`;
    }
    return candidates.find((candidate) => candidate.candidateId === id)?.displayName ?? id;
  };
  async function generate() {
    setPreview(
      await previewLink.mutateAsync({
        deviceId: record.deviceId,
        equipmentModelRevisionId: selected || null,
      }),
    );
  }
  async function apply() {
    if (preview === null) return;
    await applyLink.mutateAsync({
      deviceId: record.deviceId,
      equipmentModelRevisionId: selected || null,
      expectedVersion: preview.expectedVersion,
      previewChecksum: preview.previewChecksum,
      acknowledgeDifferences: acknowledge,
      reason,
    });
    setPreview(null);
  }
  const optionIds = Array.from(
    new Set([
      ...(record.equipmentModelRevisionId === null ? [] : [record.equipmentModelRevisionId]),
      ...candidates.map((candidate) => candidate.candidateId),
    ]),
  );
  return (
    <div className="space-y-3 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-medium">
          {t('provision.hardware.equipmentModelReference', 'Equipment model reference')}
        </h4>
        <MatchBadge status={record.catalogueMatchStatus} />
        {proposal !== null && (
          <Badge variant="outline">
            {t('provision.hardware.proposal', 'proposal: {result}').replace(
              '{result}',
              proposal.result,
            )}
          </Badge>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-[1fr_auto]">
        <Select
          aria-label={t('provision.hardware.proposedLinkLabel', 'Proposed equipment model link')}
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value);
            setPreview(null);
          }}
        >
          <option value="">
            {t('provision.hardware.unlinkRemainLegacy', 'Unlink / remain Custom/legacy')}
          </option>
          {optionIds.map((id) => (
            <option key={id} value={id}>
              {displayName(id)}
            </option>
          ))}
        </Select>
        <Button size="sm" variant="outline" onClick={() => void generate()}>
          {t('provision.hardware.previewReferenceChange', 'Preview reference change')}
        </Button>
      </div>
      {preview !== null && (
        <LinkConfirmation
          differences={preview.differences}
          snapshot={preview.catalogue ?? null}
          acknowledge={acknowledge}
          setAcknowledge={setAcknowledge}
          reason={reason}
          setReason={setReason}
          busy={applyLink.isPending}
          onApply={() => void apply()}
        />
      )}
      <MutationError error={previewLink.error ?? applyLink.error} />
    </div>
  );
}

function BindingLinkPanel({
  record,
  binding,
  proposal,
  software,
  ports,
}: {
  record: ProvisionPeripheralManualRecord;
  binding: ProvisionPeripheralManualRecord['bindings'][number];
  proposal: ProvisionLegacyMatchProposal | null;
  software: ProvisionSoftwareCatalogueComponent[];
  ports: Array<{ portId: string; label: string; kind: string; status: string; health: string }>;
}) {
  const t = useT('admin_ui');
  const previewLink = usePreviewProvisionBindingLink();
  const applyLink = useApplyProvisionBindingLink();
  const initial = {
    connectionProfileId: binding.connectionProfileId,
    endpointPortId: binding.endpointPortId,
    adapterComponentVersionId: binding.adapterComponentVersionId,
  };
  const [references, setReferences] = useState<ProvisionBindingLinkReferences>(initial);
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof previewLink.mutateAsync>
  > | null>(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [reason, setReason] = useState('');
  const connectionCandidates =
    proposal?.candidates.filter((candidate) => candidate.kind === 'connection_method') ?? [];
  const adapterCandidates =
    proposal?.candidates.filter((candidate) => candidate.kind === 'component_version') ?? [];
  const connectionIds = Array.from(
    new Set([
      ...(binding.connectionProfileId === null ? [] : [binding.connectionProfileId]),
      ...connectionCandidates.map((candidate) => candidate.candidateId),
    ]),
  );
  const adapterIds = Array.from(
    new Set([
      ...(binding.adapterComponentVersionId === null ? [] : [binding.adapterComponentVersionId]),
      ...adapterCandidates.map((candidate) => candidate.candidateId),
    ]),
  );
  const adapterName = (id: string) => {
    for (const component of software) {
      const version = component.versions.find((candidate) => candidate.componentVersionId === id);
      if (version !== undefined) {
        return `${component.displayName} ${version.semanticVersion} · ${certificationLabel(version.certification, t)}`;
      }
    }
    return adapterCandidates.find((candidate) => candidate.candidateId === id)?.displayName ?? id;
  };
  async function generate() {
    setPreview(
      await previewLink.mutateAsync({
        deviceId: record.deviceId,
        bindingId: binding.bindingId,
        references,
      }),
    );
  }
  async function apply() {
    if (preview === null) return;
    await applyLink.mutateAsync({
      deviceId: record.deviceId,
      bindingId: binding.bindingId,
      references,
      expectedVersion: preview.expectedVersion,
      previewChecksum: preview.previewChecksum,
      acknowledgeDifferences: acknowledge,
      reason,
    });
    setPreview(null);
  }
  function setReference(key: keyof ProvisionBindingLinkReferences, value: string) {
    setReferences((current) => {
      if (key === 'connectionProfileId' && value === '') {
        return {
          connectionProfileId: null,
          endpointPortId: null,
          adapterComponentVersionId: null,
        };
      }
      return { ...current, [key]: value || null };
    });
    setPreview(null);
  }
  return (
    <div className="space-y-3 rounded-md border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="font-medium">
          {t('provision.hardware.binding', 'Binding: {role}').replace(
            '{role}',
            binding.logicalRole,
          )}
        </h4>
        <MatchBadge status={binding.catalogueMatchStatus} />
        {proposal !== null && (
          <Badge variant="outline">
            {t('provision.hardware.proposal', 'proposal: {result}').replace(
              '{result}',
              proposal.result,
            )}
          </Badge>
        )}
      </div>
      <div className="grid gap-2 lg:grid-cols-3">
        <Select
          aria-label={t(
            'provision.hardware.bindingConnectionProfile',
            '{role} connection profile',
          ).replace('{role}', binding.logicalRole)}
          value={references.connectionProfileId ?? ''}
          onChange={(event) => setReference('connectionProfileId', event.target.value)}
        >
          <option value="">
            {t('provision.hardware.unlinkAllReferences', 'Unlink all managed references')}
          </option>
          {connectionIds.map((id) => (
            <option key={id} value={id}>
              {connectionCandidates.find((candidate) => candidate.candidateId === id)
                ?.displayName ?? id}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t(
            'provision.hardware.bindingAdapterVersion',
            '{role} adapter version',
          ).replace('{role}', binding.logicalRole)}
          disabled={references.connectionProfileId === null}
          value={references.adapterComponentVersionId ?? ''}
          onChange={(event) => setReference('adapterComponentVersionId', event.target.value)}
        >
          <option value="">
            {t('provision.hardware.selectAdapterVersion', 'Select proposed adapter version')}
          </option>
          {adapterIds.map((id) => (
            <option key={id} value={id}>
              {adapterName(id)}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('provision.hardware.bindingEndpointPort', '{role} endpoint port').replace(
            '{role}',
            binding.logicalRole,
          )}
          disabled={references.connectionProfileId === null}
          value={references.endpointPortId ?? ''}
          onChange={(event) => setReference('endpointPortId', event.target.value)}
        >
          <option value="">
            {t('provision.hardware.noPortExternal', 'No port / external connection')}
          </option>
          {ports.map((port) => (
            <option key={port.portId} value={port.portId}>
              {port.label} · {port.kind} · {port.status}/{port.health}
            </option>
          ))}
        </Select>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={
          references.connectionProfileId !== null && references.adapterComponentVersionId === null
        }
        onClick={() => void generate()}
      >
        {t('provision.hardware.previewLinkUnlink', 'Preview link / unlink')}
      </Button>
      {preview !== null && (
        <LinkConfirmation
          differences={preview.differences}
          snapshot={preview.catalogueSnapshots}
          acknowledge={acknowledge}
          setAcknowledge={setAcknowledge}
          reason={reason}
          setReason={setReason}
          busy={applyLink.isPending}
          onApply={() => void apply()}
        />
      )}
      <MutationError error={previewLink.error ?? applyLink.error} />
    </div>
  );
}

function LinkConfirmation({
  differences,
  snapshot,
  acknowledge,
  setAcknowledge,
  reason,
  setReason,
  busy,
  onApply,
}: {
  differences: ProvisionRawFieldDifference[];
  snapshot: unknown;
  acknowledge: boolean;
  setAcknowledge: (value: boolean) => void;
  reason: string;
  setReason: (value: string) => void;
  busy: boolean;
  onApply: () => void;
}) {
  const t = useT('admin_ui');
  return (
    <div className="space-y-3 border-t pt-3">
      <Badge variant={differences.length === 0 ? 'success' : 'outline'}>
        {differences.length === 0
          ? t('provision.hardware.exactRawMatch', 'exact raw match — references only')
          : t(
              'provision.hardware.rawDifferences',
              '{count} raw differences — remains Custom/legacy',
            ).replace('{count}', String(differences.length))}
      </Badge>
      {differences.length > 0 && <DifferenceList differences={differences} />}
      {snapshot !== null && (
        <details className="rounded-md border p-2 text-xs">
          <summary className="cursor-pointer">
            {t('provision.hardware.snapshotSummary', 'Immutable catalogue snapshot to audit')}
          </summary>
          <pre className="mt-2 max-h-72 overflow-auto bg-muted p-2">
            {prettyJson(redactForDisplay(snapshot))}
          </pre>
        </details>
      )}
      {differences.length > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={acknowledge}
            onChange={(event) => setAcknowledge(event.target.checked)}
          />
          {t(
            'provision.hardware.acknowledgeDifferences',
            'Acknowledge differences; do not rewrite raw runtime fields',
          )}
        </label>
      )}
      <Input
        required
        placeholder={t(
          'provision.hardware.referenceReasonPlaceholder',
          'Reason for explicit catalogue reference change',
        )}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      <Button
        size="sm"
        disabled={busy || reason.trim().length === 0 || (differences.length > 0 && !acknowledge)}
        onClick={onApply}
      >
        {t('provision.hardware.confirmReferenceChange', 'Confirm reference-only change')}
      </Button>
    </div>
  );
}

export function manualHardwareEditable(
  record: ProvisionPeripheralManualRecord,
): Omit<ProvisionManualUpdateInput, 'expectedVersion'> {
  return {
    classKey: record.classKey,
    vendor: record.vendor,
    model: record.model,
    serialAssetId: record.serialAssetId,
    connectionType: record.connectionType,
    connectionParameters: record.connectionParameters,
    adapterId: record.adapterId,
    supportedVersion: record.supportedVersion,
    secretReference: record.secretReference,
    bindings: record.bindings.map((binding) => ({
      bindingId: binding.bindingId,
      applicationInstanceId: binding.applicationInstanceId,
      logicalRole: binding.logicalRole,
      required: binding.required,
      config: binding.config,
    })),
  };
}

export function parseManualHardwareText(
  text: string,
  current: ProvisionPeripheralManualRecord,
): { ok: true; values: ProvisionManualUpdateInput } | { ok: false; error: string } {
  try {
    const value: unknown = JSON.parse(text);
    if (!isRecord(value))
      return { ok: false, error: 'The complete raw record must be a JSON object.' };
    const requiredStrings = ['classKey', 'connectionType', 'adapterId'] as const;
    for (const key of requiredStrings) {
      if (typeof value[key] !== 'string' || value[key].length === 0) {
        return { ok: false, error: `${key} must be a non-empty string.` };
      }
    }
    if (!isRecord(value['connectionParameters'])) {
      return { ok: false, error: 'connectionParameters must be a JSON object.' };
    }
    if (!Array.isArray(value['bindings']))
      return { ok: false, error: 'bindings must be an array.' };
    const bindings: ProvisionManualUpdateInput['bindings'] = [];
    for (const [index, raw] of value['bindings'].entries()) {
      if (!isRecord(raw) || !isRecord(raw['config'])) {
        return { ok: false, error: `bindings[${index}] and its config must be JSON objects.` };
      }
      if (
        typeof raw['bindingId'] !== 'string' ||
        typeof raw['logicalRole'] !== 'string' ||
        typeof raw['required'] !== 'boolean' ||
        !(raw['applicationInstanceId'] === null || typeof raw['applicationInstanceId'] === 'string')
      ) {
        return {
          ok: false,
          error: `bindings[${index}] has an invalid identity, scope, role, or required flag.`,
        };
      }
      bindings.push({
        bindingId: raw['bindingId'],
        applicationInstanceId: raw['applicationInstanceId'],
        logicalRole: raw['logicalRole'],
        required: raw['required'],
        config: raw['config'],
      });
    }
    const currentIds = current.bindings.map((binding) => binding.bindingId).sort();
    const requestedIds = bindings.map((binding) => binding.bindingId).sort();
    if (JSON.stringify(currentIds) !== JSON.stringify(requestedIds)) {
      return {
        ok: false,
        error:
          'Every stable bindingId must remain present exactly once; use assignment for topology changes.',
      };
    }
    for (const key of [
      'vendor',
      'model',
      'serialAssetId',
      'supportedVersion',
      'secretReference',
    ] as const) {
      if (!(value[key] === null || typeof value[key] === 'string')) {
        return { ok: false, error: `${key} must be a string or null.` };
      }
    }
    return {
      ok: true,
      values: {
        expectedVersion: current.version,
        classKey: value['classKey'] as string,
        vendor: value['vendor'] as string | null,
        model: value['model'] as string | null,
        serialAssetId: value['serialAssetId'] as string | null,
        connectionType: value['connectionType'] as string,
        connectionParameters: value['connectionParameters'],
        adapterId: value['adapterId'] as string,
        supportedVersion: value['supportedVersion'] as string | null,
        secretReference: value['secretReference'] as string | null,
        bindings,
      },
    };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : String(error) };
  }
}

function DifferenceList({ differences }: { differences: ProvisionRawFieldDifference[] }) {
  const t = useT('admin_ui');
  return (
    <div className="space-y-2">
      {differences.map((difference) => (
        <div
          key={`${difference.path}-${difference.kind}`}
          className="rounded-md border p-2 text-xs"
        >
          <code>{difference.path}</code> · {difference.kind}
          <div className="mt-1 grid gap-2 md:grid-cols-2">
            <pre className="overflow-auto bg-muted p-2">
              {t('provision.composition.diffBefore', 'before:')}{' '}
              {difference.before.present
                ? prettyJson(redactForDisplay(difference.before.value))
                : '<absent>'}
            </pre>
            <pre className="overflow-auto bg-muted p-2">
              {t('provision.composition.diffAfter', 'after:')}{' '}
              {difference.after.present
                ? prettyJson(redactForDisplay(difference.after.value))
                : '<absent>'}
            </pre>
          </div>
        </div>
      ))}
    </div>
  );
}

function MatchBadge({
  status,
}: {
  status: ProvisionPeripheralManualRecord['catalogueMatchStatus'];
}) {
  const t = useT('admin_ui');
  return (
    <Badge variant={status === 'managed' ? 'success' : 'outline'}>
      {status === 'custom_legacy'
        ? t('provision.composition.customLegacy', 'Custom/legacy')
        : status.replaceAll('_', ' ')}
    </Badge>
  );
}

function MutationError({ error }: { error: unknown }) {
  if (error === null || error === undefined) return null;
  return <ErrorPanel message={errorMessage(error)} />;
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  );
}

function errorMessage(error: unknown) {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

function prettyJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
